import { Root } from '@athena/forge';
import { createRoot } from 'react-dom/client';
import React from 'react';
import WidgetContextProvider from '../components/context/WidgetContextProvider';
import { IWidgetContext } from '../components/context/WidgetContext';
import ErrorBoundary from '../components/common/ErrorBoundary';
import BackToLogin from '../components/common/BackToLogin';
import ProxyButton from '../components/common/ProxyButton';
import { FOOTER_LINK_AMPLITUDE_EVENT_SUFFIX } from '../constants';
import AmplitudeUtils from './amplitude-utils';
import ChooseDifferentFactorButton from '../components/lwa-profile/ChooseDifferentFactorButton';

export interface RenderReactComponentArgs {
  id: string;
  component: React.ReactNode;
  container: HTMLElement;
  className?: string;
}

export type FactoryReturnType = (args: RenderReactComponentArgs) => void;

export default function factory(widgetContext: IWidgetContext): FactoryReturnType {
  return function renderReactComponent({ id, component, container, className = '' }) {
    const forgifiedId = `athena-${id}`;
    if (document.getElementById(forgifiedId)) {
      return;
    }
    container.id = forgifiedId;
    const root = createRoot(container);
    root.render(
      <ErrorBoundary>
        <WidgetContextProvider {...widgetContext}>
          <Root className={className}>{component}</Root>
        </WidgetContextProvider>
      </ErrorBoundary>
    );
  };
}

export function createWrapper({ hidden = true } = {}) {
  const wrapper = jQueryCourage('<div></div>');
  if (hidden) wrapper.addClass('hide-element');
  return wrapper[0];
}

function getFooterLinkText(footerLink: HTMLElement) {
  return footerLink.textContent || '';
}
interface GenerateFootersProps {
  footerLinks: JQuery;
  alwaysDisplayBackToSignIn: boolean;
  additionalOnClick?: () => void;
}

export function generateFooters({ footerLinks, alwaysDisplayBackToSignIn, additionalOnClick }: GenerateFootersProps) {
  let signOutLinkFound = false;
  const reactFooters: React.ReactNode[] = [];
  footerLinks?.each((index, footerLink) => {
    const onClick = async () => {
      const dataSe = footerLink?.dataset?.se ?? '';
      const amplitudeSuffix = FOOTER_LINK_AMPLITUDE_EVENT_SUFFIX[dataSe];
      amplitudeSuffix && (await AmplitudeUtils.logCustomEvent(`Footer::${amplitudeSuffix}`, {}));
      additionalOnClick?.();
    };
    const buttonLabel = getFooterLinkText(footerLink);
    const oktaButton = jQueryCourage(footerLink);
    if (oktaButton.is('[data-se=signout-link]') || oktaButton.is('[data-se=cancel]')) {
      signOutLinkFound = true;
      reactFooters.push(
        <div key={index}>
          <BackToLogin oktaButton={oktaButton} />
          <br />
        </div>
      );
    }
    else if (oktaButton.is('[data-se="back-link"]') || oktaButton.is('[data-se="switchAuthenticator"]')) {
      reactFooters.push(
        <div key={index}>
          <ChooseDifferentFactorButton oktaButton={oktaButton} onClick={onClick} buttonLabel={buttonLabel} />
        </div>
      );
    }
    else {
      reactFooters.push(
        <div key={index}>
          <ProxyButton
            textToOverride={buttonLabel}
            oktaButton={oktaButton}
            variant="tertiary"
            size="small"
            fullWidth={false}
            onClick={onClick}
          />
          <br />
        </div>
      );
    }
  });

  if (!signOutLinkFound && alwaysDisplayBackToSignIn) {
    reactFooters.push(<BackToLogin key={'back-to-login'} />);
  }
  return reactFooters;
}
